import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@xometry/auth-context';
import { useQuery } from '@apollo/client';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Flex, theme, Text, Spinner, Link } from '@xometry/xometry_loft';
import TeamMemberManagement from './TeamMemberManagement';
import Notification from '../../Notification';
import SideBar from './SideBar';
import TeamInfoPanel from './TeamInfoPanel';
import AlertPanel from './AlertPanel';
import NewCreatedTeamModal from './NewCreatedTeamModal';

import {
  GetOrganizationByIdQuery,
  GetOrganizationUserPermissionsForMeQuery,
  GetOrganizationUserPermissionsForMeQueryResult,
  Organization,
  OrganizationPermission,
  GetOrganizationByIdQueryResult,
  GetOrganizationUsersQuery,
  GetOrganizationUsersQueryResult,
  GetUsersOrganizationsByIdentityQuery,
  GetUsersOrganizationsByIdentityQueryResult,
  GetUsersOrganizationsByIdentityQueryVariables,
} from '../../../schema/generated/schema';
import NotificationContext from '../../../contexts/Notification';
import RestrictedPanel from '../../../components/RestrictedAccess';
import { WWW_ORIGIN } from '../../../config';
import TeamSharingDefaults from './TeamSharingDefaults';
import TeamNotifications from './TeamNotifications';
import TeamNotFoundPanel from './TeamNotFound';
import TeamShipping from './TeamShipping';
import QuotePresets from './QuotePresets';

const getAlertMessage = (
  isStaff: boolean,
  isOrgMember: boolean,
  hasWriteOrgPermission: boolean,
  isArchived?: boolean
) => {
  if (isArchived) {
    return 'You are Viewing a Deactivated Team';
  } else if (isStaff) {
    return 'You are viewing this page as Staff.';
  } else if (isOrgMember && !hasWriteOrgPermission) {
    return 'Roles and Permissions can only be managed by Admins.';
  } else return '';
};
const TeamSettings = () => {
  const { user, isLoading, isStaff } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const initialAlertSettings = {
    show: false,
    msg: '',
  };

  const [isOrgMember, setIsOrgMember] = useState(false);
  const [hasWriteOrgPermission, setHasWriteOrgPermission] = useState(false);
  const [userOrgsLoading, setUserOrgsLoading] = useState(true);
  const [organization, setOrganization] = useState<Organization>();
  const [alertPanel, setAlertPanel] = useState(initialAlertSettings);
  const [pageReadOnly, setPageReadOnly] = useState(false);
  const [canViewContent, setCanViewContent] = useState(false);
  const { orgId: orgIdParam } = useParams();
  const { notification, handleNotification } = useContext(NotificationContext);
  const [newCreatedTeamModalOpen, setNewCreatedTeamModalOpen] = useState(
    Boolean(searchParams.get('create'))
  );
  const [activeView, setActiveView] = useState<string>('teamManagementView');
  const [onboardingPanelKey, setOnboardingPanelKey] = useState<number>(0);
  const [completedOnboardingActions, setCompletedOnboardingActions] = useState<
    Set<string>
  >(new Set());

  const handleOnboardingActionCompleted = (actionType: string) => {
    // Only trigger reload if this action type hasn't been completed before
    if (!completedOnboardingActions.has(actionType)) {
      setOnboardingPanelKey((prev) => prev + 1);
      setCompletedOnboardingActions((prev) => new Set([...prev, actionType]));
    }
  };

  const { loading: isLoadingOrg } = useQuery(GetOrganizationByIdQuery, {
    variables: {
      id: orgIdParam,
    },
    onCompleted: (data: GetOrganizationByIdQueryResult) => {
      setOrganization(data.getOrganizationById);
      setPageReadOnly(!!data.getOrganizationById.isArchived);
    },
  });
  const { data: organizationUsersData, loading: organizationUsersLoading } =
    useQuery<GetOrganizationUsersQueryResult>(GetOrganizationUsersQuery, {
      skip: !organization,
      variables: {
        orgId: organization?.id,
      },
    });
  const { refetch: refetchInactiveOrganizations } = useQuery<
    GetUsersOrganizationsByIdentityQueryResult,
    GetUsersOrganizationsByIdentityQueryVariables
  >(GetUsersOrganizationsByIdentityQuery, {
    skip: !user?.id || !organization?.id,
    variables: {
      // userId won't ever be '' because skip is configured to only run query when userId is defined
      userId: user?.id || '',
      application: 'xometry',
      isArchived: !!organization?.isArchived,
    },
    onCompleted: (data) => {
      const isUserInOrg = data.getUsersOrganizationsByIdentity.some(
        (org) => org.id == organization?.id
      );
      setIsOrgMember(isUserInOrg);
      setUserOrgsLoading(false);
    },
  });

  const {
    loading: userOrganizationPermissionsIsLoading,
    data: userOrganizationPermissions,
  } = useQuery<GetOrganizationUserPermissionsForMeQueryResult>(
    GetOrganizationUserPermissionsForMeQuery,
    {
      skip: !organization,
      variables: {
        orgId: organization?.id,
      },
    }
  );

  useEffect(() => {
    if (searchParams.has('create')) {
      searchParams.delete('create');
      setSearchParams(searchParams);
    }
    if (searchParams.has('sidebar')) {
      switch (searchParams.get('sidebar')) {
        case 'management':
          setActiveView('teamManagementView');
          break;
        case 'sharing':
          setActiveView('sharingDefaultsView');
          break;
        case 'notifications':
          setActiveView('notificationsView');
          break;
        case 'quote-presets':
          setActiveView('quotePresetsView');
          break;
        case 'shipping':
          setActiveView('teamShippingView');
          break;
      }
    }
  }, []);

  useEffect(() => {
    if (user) {
      // if user already has write permissions we do not want to take it away.
      if (!hasWriteOrgPermission) {
        setHasWriteOrgPermission(isStaff());
      }
      if (window.analytics) {
        window.analytics.track('Team Member Management Page Viewed', {
          isStaff: isStaff(),
          userId: user.id,
          OrganizationId: organization?.id,
          email: user.emailAddress,
        });
      }
    }
  }, [user]);

  useEffect(() => {
    if (userOrganizationPermissions) {
      const permissions =
        userOrganizationPermissions.getOrganizationUserPermissionsForMe || [];
      const hasWrite = !!permissions.find(
        (permission: OrganizationPermission | null) =>
          permission?.name === 'write:organizations'
      );
      // if user already has write permissions we do not want to take it away.
      if (!hasWriteOrgPermission) {
        setHasWriteOrgPermission(hasWrite);
      }
    }
  }, [userOrganizationPermissions]);

  const isAuthUserStaff = isStaff();
  useEffect(() => {
    const alertMessage = getAlertMessage(
      isStaff(),
      isOrgMember,
      hasWriteOrgPermission,
      !!organization?.isArchived
    );
    if (alertPanel.msg !== alertMessage) {
      setAlertPanel({
        msg: alertMessage,
        show: !!alertMessage, //&& !organization?.isArchived,
      });
    }
  }, [isAuthUserStaff, isOrgMember, hasWriteOrgPermission, organization]);

  useEffect(() => {
    setCanViewContent(isStaff() || isOrgMember);
  }, [isOrgMember, userOrgsLoading, isLoadingOrg]);

  if (
    isLoading ||
    isLoadingOrg ||
    organizationUsersLoading ||
    userOrganizationPermissionsIsLoading ||
    userOrgsLoading
  ) {
    return <Spinner />;
  }

  if (organization?.isArchived && !hasWriteOrgPermission) {
    return <TeamNotFoundPanel />;
  }

  return (
    <Box
      id="team-settings-wrapper"
      width={['340px', '500px', '840px', '1129px']}
      margin="0 auto"
      paddingBottom="80px"
    >
      {organization?.isArchived && (
        <Box marginTop={theme.space[6]}>
          <AlertPanel
            msg={alertPanel.msg}
            isStaff={isStaff() || hasWriteOrgPermission}
            activeView={activeView}
            org={organization}
            refetchInactiveOrganizations={refetchInactiveOrganizations}
          />
        </Box>
      )}

      <Flex
        justifyContent="space-between"
        alignItems="center"
        marginY={theme.space[6]}
        paddingBottom={theme.space[4]}
        borderBottom={`1px solid ${theme.colors.darkBlues[25]}`}
      >
        <Text
          className="title-wrapper"
          kind="page-title"
          color={theme.colors.darkBlues[100]}
        >
          Team Settings
        </Text>
        <Link
          href={`${WWW_ORIGIN}/quoting/org/${organization?.id}`}
          textAlign="right"
          fontSize={theme.fontSizes[2]}
        >
          View Team Dashboard <i className="fa-regular fa-arrow-right"></i>
        </Link>
      </Flex>
      <Flex flexDirection={['column', 'column', 'row']} gap={theme.space[8]}>
        <Flex className="sidebar-wrapper" minWidth="360px">
          <SideBar
            org={organization}
            active={activeView}
            setActiveView={setActiveView}
            onboardingPanelKey={onboardingPanelKey}
          />
        </Flex>
        <Flex
          className="main-panel-wrapper"
          flexDirection="column"
          width="100%"
        >
          {notification.length > 0 &&
            notification.map((notification, index) => (
              <Notification
                key={index}
                text={notification.text}
                type={notification.type}
                handleClose={handleNotification}
                notificationIndex={index}
                duration={5000}
              />
            ))}
          {canViewContent && activeView === 'teamManagementView' && (
            <>
              {alertPanel.show && !isStaff() && !organization?.isArchived && (
                <AlertPanel msg={alertPanel.msg} />
              )}
              <TeamInfoPanel
                hasWriteOrgPermission={hasWriteOrgPermission}
                isStaff={isStaff()}
                org={organization}
              />
              <TeamMemberManagement
                hasWriteOrgPermission={hasWriteOrgPermission}
                isStaff={isStaff()}
                org={organization}
                organizationUsers={
                  organizationUsersData?.getOrganizationUsers || []
                }
                onOnboardingActionCompleted={() =>
                  handleOnboardingActionCompleted('team-management')
                }
              />
            </>
          )}
          {canViewContent && activeView === 'sharingDefaultsView' && (
            <TeamSharingDefaults
              userId={user?.id || ''}
              orgId={organization?.id}
              isStaff={isStaff()}
              pageReadOnly={pageReadOnly}
              onOnboardingActionCompleted={() =>
                handleOnboardingActionCompleted('sharing')
              }
            />
          )}
          {canViewContent && activeView === 'notificationsView' && (
            <TeamNotifications
              userId={user?.id || ''}
              orgId={organization?.id}
              isOrgMember={isOrgMember}
              pageReadOnly={pageReadOnly}
              onOnboardingActionCompleted={() =>
                handleOnboardingActionCompleted('notifications')
              }
            />
          )}
          {canViewContent &&
            activeView === 'teamShippingView' &&
            organization?.id && (
              <TeamShipping
                orgId={organization?.id}
                onOnboardingActionCompleted={() =>
                  handleOnboardingActionCompleted('shipping')
                }
              />
            )}
          {canViewContent && activeView === 'quotePresetsView' && (
            <QuotePresets
              userId={user?.id || ''}
              orgId={organization?.id || ''}
              isStaff={isStaff()}
              hasWriteOrgPermission={hasWriteOrgPermission}
              onOnboardingActionCompleted={() =>
                handleOnboardingActionCompleted('quote-presets')
              }
            />
          )}
          {!canViewContent && (
            <Flex
              width="100%"
              backgroundColor={theme.colors.white}
              paddingBottom={theme.space[5]}
              borderRadius="3px"
              justifyContent="center"
            >
              <RestrictedPanel redirectUrl={`${WWW_ORIGIN}/quoting/home`} />
            </Flex>
          )}
        </Flex>
      </Flex>
      <NewCreatedTeamModal
        newCreatedTeamModalOpen={newCreatedTeamModalOpen}
        setNewCreatedTeamModalOpen={setNewCreatedTeamModalOpen}
        organization={organization}
      />
    </Box>
  );
};

export default TeamSettings;
